import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import PropTypes from 'prop-types';
import { Component, useEffect, useMemo, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';

import { DEFAULT_STREAM_DESCRIPTION } from '../helpers/constants';
import decodeURIComponentSafe from '../helpers/decodeURIComponentSafe';
import dynamicallySort from '../helpers/dynamicallySort';
import { RecordingSource, StreamCheckmarkType } from '../helpers/enums';
import get from '../helpers/get';
import { routerLocationShape, userShape } from '../helpers/propTypeShapes';
import secondsToTimeString from '../helpers/secondsToTimeString';
import trackMixPanelEvent from '../helpers/trackMixPanelEvent';
import useDisableVideoDownload from '../helpers/useDisableVideoDownload';
import ConfirmationModal from './ConfirmationModal';
import DownloadButtonPicker from './DownloadButton/DownloadButtonPicker';
import ShareButton from './ShareButton';

import 'react-toastify/dist/ReactToastify.css';
import '../style/clips.css';

momentDurationFormatSetup(moment);

function millisecondsToSeconds(s) {
  return parseFloat(s / 1000);
}

const ClipListItem = ({
  clip,
  pollForClipComplete,
  handleTitleChange,
  handleEnterKey,
  handleDescriptionChange,
  saveToMediaLibrary,
  requestDeleteClip,
  broadcastHash,
  broadcastId,
}) => {
  const originalTitle = useMemo(() => decodeURIComponentSafe(clip.title), [clip.title]);
  const originalDescription = useMemo(() => decodeURIComponentSafe(clip.description), [clip.description]);

  const [clipDescription, setClipDescription] = useState(decodeURIComponentSafe(clip.description));
  const [clipTitle, setClipTitle] = useState(decodeURIComponentSafe(clip.title));

  const [displayCheckMark, setDisplayCheckMark] = useState(StreamCheckmarkType.NONE);
  const [showDuration, setShowDuration] = useState(true);
  const { disableRightClickFunc } = useDisableVideoDownload();
  const playerRef = useRef();

  const pagePath = `/broadcastdetails/broadcast/${broadcastHash}/${broadcastId}/clips`;
  const createdAt = moment(clip.createdAt).format('MMM Do YYYY, h:mm a');
  const { user = {}, id: primaryKey } = clip;
  const userName = user.name ?? '';
  const clipDuration = millisecondsToSeconds(clip.durationMillis);

  let allowContentEditing;
  let deleteTooltipCopy;
  let forbidDeletion;
  let clipThumbnail;
  const emptyDescription = '';
  let downloadBtn = '';

  const handleOnClickToPlay = (event) => {
    const { videojs } = window;

    if (!playerRef.current) {
      const player = event.target;

      playerRef.current = videojs(player, { controls: true }, () => {
        player.play();
        setShowDuration(false);
      });
    }

    const isVideoPaused = playerRef.current.paused();
    setShowDuration(isVideoPaused);
  };

  if (clip.status === 'COMPLETED' && clip.file) {
    clipThumbnail = (
      <div className="clip-container" data-hash={clip.sessionHash} role="button">
        <video
          id={`vid_${clip.sessionHash}`}
          className="video-js"
          onClick={handleOnClickToPlay}
          preload="none"
          poster={clip.thumbnail}
          onContextMenu={disableRightClickFunc}
        >
          <source src={clip.file} type="video/mp4" />
        </video>
        {showDuration && (
          <div id={`duration_${clip.sessionHash}`} className="clip-duration">
            {moment.duration(clipDuration, 'seconds').format()}
          </div>
        )}
      </div>
    );
    downloadBtn = (
      <DownloadButtonPicker
        type="clips"
        namingConvention={RecordingSource.CLIPS}
        newTitle={clipTitle}
        downloadFile={clip}
        pagePath={pagePath}
      />
    );
  } else if (clip.status === 'PROGRESSING') {
    pollForClipComplete(clip.sessionHash);
    clipThumbnail = (
      <div className="clip-container">
        <div className="clip-thumbnail empty">
          <div className="clip-spinner">
            <span className="clip-spinner-text">Processing clip</span>
          </div>
        </div>
      </div>
    );
  }

  if (
    sessionStorage.getItem('currentAccountRole') !== 'ADMIN' &&
    clip.user.id !== parseInt(sessionStorage.getItem('userId'), 10)
  ) {
    allowContentEditing = 'false';
    deleteTooltipCopy = 'No permission to delete';
    forbidDeletion = true;
  } else {
    allowContentEditing = 'true';
    deleteTooltipCopy = 'Delete';
    forbidDeletion = false;
  }

  const onHandleDescriptionChange = async (event) => {
    const newDescriptionValue = event.target.innerText;

    if (newDescriptionValue !== clipDescription) {
      const { error } = await handleDescriptionChange(newDescriptionValue, primaryKey);
      if (error) {
        setClipDescription(originalDescription);
        return;
      }
      setDisplayCheckMark(StreamCheckmarkType.DESCRIPTION);
    }

    const desc = newDescriptionValue === '' ? clip.defaultDescription : newDescriptionValue;
    setClipDescription(desc);
  };

  const onHandleTitleChange = async (event) => {
    const newTitleValue = event.target.innerText;

    if (newTitleValue !== clipTitle && newTitleValue !== '') {
      const { error } = await handleTitleChange(newTitleValue, primaryKey);
      if (error) {
        setClipTitle(originalTitle);
        return;
      }
      setDisplayCheckMark(StreamCheckmarkType.TITLE);
    }

    setClipTitle(newTitleValue);
  };

  const handleOnDescriptionClick = (event) => {
    const currentDescription = event.target.innerText;
    if (currentDescription === DEFAULT_STREAM_DESCRIPTION) {
      setClipDescription('');
    }
  };

  useEffect(() => {
    if (!displayCheckMark) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setDisplayCheckMark(StreamCheckmarkType.NONE);
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [displayCheckMark]);

  useEffect(() => {
    if (clipTitle === '') {
      setClipTitle(originalTitle);
    }
  }, [clipTitle, originalTitle]);

  useEffect(
    () => () => {
      const player = playerRef.current;
      if (player) {
        player.dispose();
      }
    },
    []
  );

  return (
    <div
      id={`clipRow_${clip.sessionHash}`}
      className="clips-row"
      data-clip-hash={clip.sessionHash}
      key={clip.sessionHash}
    >
      <div className="clips-cell-thumbnail" data-clip-hash={clip.sessionHash}>
        {clipThumbnail}
      </div>
      <div className="clip-info-holder">
        <div className="clips-cell-info">
          &#8203;
          <span
            className="clip-name"
            contentEditable={allowContentEditing}
            data-clipid={primaryKey}
            data-cliptitle={clipTitle}
            onBlur={onHandleTitleChange}
            onKeyDown={handleEnterKey}
            suppressContentEditableWarning
            role="textbox"
            tabIndex={0}
          >
            {clipTitle}
          </span>
          {StreamCheckmarkType.TITLE === displayCheckMark && <span className="title-success-checkmark active" />}
          &#8203;
          <br className="border-shim" />
          <span className="clip-creator">Created by {userName}</span>
          &nbsp;&middot;&nbsp;
          <span className="clip-created-on">{createdAt}</span>
          <br />
          &#8203;
          <span
            className={`clip-description ${emptyDescription}`}
            contentEditable={allowContentEditing}
            data-clipdescription={clipDescription}
            data-clipid={primaryKey}
            onClick={handleOnDescriptionClick}
            onBlur={onHandleDescriptionChange}
            onKeyDown={handleEnterKey}
            suppressContentEditableWarning
            tabIndex={0}
            role="textbox"
          >
            {clipDescription}
          </span>
          {StreamCheckmarkType.DESCRIPTION === displayCheckMark && (
            <span className="description-success-checkmark active" />
          )}
          &#8203;
        </div>
        <div className="clips-cell-options">
          <div id={`menu_${clip.sessionHash}`} className="options-menu">
            <ul className="broadcast-options">
              <ShareButton videoId={primaryKey} videoType="CLIP" duration={clipDuration} />
              {downloadBtn}
              <li className="broadcast-option library-save">
                <button
                  type="button"
                  data-for="clips-tooltip"
                  data-tip="Send to Studio"
                  onClick={saveToMediaLibrary}
                  data-clip-hash={clip.sessionHash}
                  aria-label="Send Clip to Studio"
                />
              </li>
              <li className="broadcast-option delete" data-for="clips-tooltip" data-tip={deleteTooltipCopy}>
                <button
                  type="button"
                  className="bin-icon"
                  disabled={forbidDeletion}
                  onClick={requestDeleteClip}
                  data-clip-id={primaryKey}
                  data-clip-hash={clip.sessionHash}
                  aria-label="Delete Clip"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

ClipListItem.propTypes = {
  clip: PropTypes.shape({
    createdAt: PropTypes.number.isRequired,
    defaultDescription: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    durationMillis: PropTypes.number.isRequired,
    file: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    sessionHash: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    thumbnail: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    user: userShape.isRequired,
  }).isRequired,
  handleTitleChange: PropTypes.func.isRequired,
  handleEnterKey: PropTypes.func.isRequired,
  handleDescriptionChange: PropTypes.func.isRequired,
  pollForClipComplete: PropTypes.func.isRequired,
  saveToMediaLibrary: PropTypes.func.isRequired,
  requestDeleteClip: PropTypes.func.isRequired,
  broadcastHash: PropTypes.string.isRequired,
  broadcastId: PropTypes.number.isRequired,
};

class Clips extends Component {
  constructor(props) {
    super(props);
    this.state = {
      clips: [],
      clipToDelete: null,
      completedClips: [],
      confirmationModalIsOpen: false,
    };

    this.deleteClip = this.deleteClip.bind(this);
    this.handleDescriptionChange = this.handleDescriptionChange.bind(this);
    this.handleTitleChange = this.handleTitleChange.bind(this);
    this.pollForClipComplete = this.pollForClipComplete.bind(this);
    this.requestDeleteClip = this.requestDeleteClip.bind(this);
    this.saveToMediaLibrary = this.saveToMediaLibrary.bind(this);
  }

  componentDidMount() {
    this.fetchViewData();
    window.addEventListener('newClip', (event) => {
      this.pollForClipExistence(event.detail);
    });
  }

  componentDidUpdate() {
    ReactTooltip.rebuild();
  }

  componentWillUnmount() {
    window.removeEventListener('newClip', (event) => {
      this.pollForClipExistence(event.detail);
    });
  }

  handleClipInfoChange(params, clipId) {
    return fetch(`${this.props.APIBaseURL}/clips/${clipId}`, params)
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'clips handleClipInfoChange',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from handleClickClips (Clips). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        if (response.status === 403) {
          const message = 'You do not have permission to make this change.';
          toast.error(message);
          throw new Error(message);
        }
        return response.json();
      })
      .then((data) => {
        const { clips } = this.state;
        const { getPrimaryKey } = this.props;

        const newClips = clips.map((clip) => {
          // eslint-disable-next-line no-underscore-dangle
          const primaryKey = getPrimaryKey(clip._links.self);
          if (primaryKey === clipId) {
            // data new title/description maybe be unset. The current clip has already been normalized.
            const description = get(data, 'description', clip.description);
            const title = get(data, 'title', clip.title);
            return {
              ...clip,
              description,
              title,
            };
          }

          return clip;
        });

        this.setState({ clips: newClips });
        return data;
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error: ', error);
        return { error: error.message };
      });
  }

  handleDescriptionChange(description, clipId) {
    const params = {
      method: 'PATCH',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        description,
      }),
    };

    return this.handleClipInfoChange(params, clipId);
  }

  handleEnterKey(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      event.target.blur();
    }
  }

  handleTitleChange(title, clipId) {
    const params = {
      method: 'PATCH',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title,
      }),
    };

    return this.handleClipInfoChange(params, clipId);
  }

  closeModal(modalName) {
    if (modalName === 'yousure') {
      this.setState({ confirmationModalIsOpen: false });
    }
  }

  deleteClip() {
    const clipId = this.state.clipToDelete;
    const params = {
      method: 'DELETE',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
    };

    fetch(`${this.props.APIBaseURL}/clip/${clipId}`, params)
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'deleteClip (Clips)',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from deleteClip (Clips). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
        } else if (response.status === 400) {
          toast.error('Something went wrong');
        } else {
          const { clips } = this.state;
          const newClips = clips.filter((item) => item.id !== clipId);

          toast.success('Clip deleted');
          this.setState({
            clips: newClips,
            clipToDelete: null,
          });
        }
        this.closeModal('yousure');
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('delete clip API call failed. ', error);
        toast.error('Something went wrong');
        this.closeModal('yousure');
      });
  }

  fetchViewData() {
    const params = {
      method: 'GET',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
    };
    const isBroadcast = this.props.streamOrBroadcast === 'broadcast';
    const streamOrBroadcast = isBroadcast ? 'by-broadcast' : 'by-stream';

    fetch(
      `${this.props.APIBaseURL}/clips/search/${streamOrBroadcast}?id=${this.props.subjectId}&projection=list`,
      params
    )
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'clips search',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from fetchViewData (Clips). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then((data) => {
        const { _embedded = {} } = data;
        const { clips = [] } = _embedded;
        const sortedClips = clips
          .sort(dynamicallySort('createdAt', 'desc'))
          .map((clip) => {
            const startTime = secondsToTimeString(millisecondsToSeconds(clip.startTime));
            const endTime = secondsToTimeString(millisecondsToSeconds(clip.endTime));
            const defaultTitle = `Clip ${startTime} to ${endTime}`;
            return {
              ...clip,
              // eslint-disable-next-line no-underscore-dangle
              id: this.props.getPrimaryKey(clip._links.self),
              title: get(clip, 'title', defaultTitle),
              description: get(clip, 'description', DEFAULT_STREAM_DESCRIPTION),
              defaultTitle,
              defaultDescription: DEFAULT_STREAM_DESCRIPTION,
            };
          })
          .filter((clip) => clip.status !== 'ERROR');

        this.setState({ clips: sortedClips });
      });
  }

  hideDuration(event) {
    const el = event.target.closest('.clip-container');
    const id = `duration_${el.dataset.hash}`;
    const duration = document.getElementById(id);

    if (duration) {
      duration.remove();
    }
  }

  openModal(modalName) {
    if (modalName === 'yousure') {
      this.setState({ confirmationModalIsOpen: true });
    }
  }

  pollForClipComplete(clipHash) {
    const params = {
      method: 'GET',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
    };
    // let polling = false;

    fetch(`${this.props.APIBaseURL}/clips/search/by-session?sessionHash=${clipHash}&projection=list`, params)
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'clips search-by-session',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from pollForClipComplete (Clips). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        return response.json();
      })
      .then((data) => {
        if (data && data.status === 'ERROR') {
          const { clips, completedClips } = this.state;
          const newClips = clips.filter((clip) => clip.sessionHash !== clipHash);

          this.setState({
            completedClips: [...completedClips, clipHash],
            clips: newClips,
          });
          return;
        }

        if (data && data.status === 'PROGRESSING') {
          setTimeout(() => {
            if (!this.state.completedClips.includes(clipHash)) {
              this.pollForClipComplete(clipHash);
            }
          }, 10000);
          return;
        }

        if (data && data.status === 'COMPLETED' && !this.state.completedClips.includes(clipHash)) {
          const { clips } = this.state;
          const newClips = clips.map((clip) => {
            const { status, file, thumbnail, durationMillis, endTime } = data;
            const clipOverride =
              clip.sessionHash === clipHash ? { status, file, thumbnail, durationMillis, endTime } : {};

            return { ...clip, ...clipOverride };
          });

          this.setState((prevState) => ({
            completedClips: [...prevState.completedClips, clipHash],
            clips: newClips,
          }));
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error: ', error);
      });
  }

  pollForClipExistence(newClipHash) {
    const params = {
      method: 'GET',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
    };
    let polling = false;

    fetch(`${this.props.APIBaseURL}/clips/search/by-session?sessionHash=${newClipHash}`, params)
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'clips search-by-session',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from pollForClipExistence (Clips). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
          return undefined;
        }
        if (response.status === 404) {
          polling = true;
          setTimeout(() => {
            this.pollForClipExistence(newClipHash);
          }, 5000);
          return undefined;
        }
        polling = false;
        return response.json();
      })
      .then(() => {
        if (!polling) {
          this.fetchViewData();
        }
      });
  }

  requestDeleteClip(event) {
    const { clipId } = event.target.dataset;

    this.setState({
      clipToDelete: clipId,
    });
    this.openModal('yousure');
  }

  saveToMediaLibrary(event) {
    const { target } = event;
    const sessionHash = target.dataset.clipHash;
    const params = {
      method: 'POST',
      headers: {
        authorization: sessionStorage.getItem('authToken'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionHash,
      }),
    };

    fetch(`${this.props.APIBaseURL}/clip/to-asset`, params)
      .then((response) => {
        if (response.status === 401) {
          this.props.trackEvent('401', {
            source: 'clips to-asset',
          });
          // eslint-disable-next-line no-console
          console.error('Received 401 response from saveToMediaLibrary (Clips). Redirecting to login.');
          this.props.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
        } else if (response.status === 400) {
          toast.error('This clip already exists in your media library');
        } else if (response.status === 200) {
          trackMixPanelEvent('Add clip to library');
          toast.success('Clip sent successfully');
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error: ', error);
      });
  }

  render() {
    const { clips } = this.state;

    if (clips.length === 0) {
      return (
        <ul id="clipRow_empty" className="clips-row" key="empty_clip">
          <li>
            <span>No clips available for this broadcast.</span>
          </li>
        </ul>
      );
    }

    return (
      <div className="content-container clips-content-container">
        <div className="clips-table">
          {clips.map((clip) => (
            <ClipListItem
              key={`${clip.id}-${clip.status}`}
              clip={clip}
              pollForClipComplete={this.pollForClipComplete}
              handleTitleChange={this.handleTitleChange}
              handleEnterKey={this.handleEnterKey}
              handleDescriptionChange={this.handleDescriptionChange}
              saveToMediaLibrary={this.saveToMediaLibrary}
              requestDeleteClip={this.requestDeleteClip}
              broadcastHash={this.props.broadcastHash}
              broadcastId={this.props.broadcastId}
            />
          ))}
        </div>

        <ConfirmationModal
          isOpen={this.state.confirmationModalIsOpen}
          handleOnClose={() => this.closeModal('yousure')}
          handleOnConfirm={this.deleteClip}
          resourceDescription="Do you really want to delete this clip? This cannot be undone."
        />

        <ReactTooltip id="clips-tooltip" effect="solid" type="light" border />
      </div>
    );
  }
}

Clips.propTypes = {
  APIBaseURL: PropTypes.string.isRequired,
  getPrimaryKey: PropTypes.func.isRequired,
  location: routerLocationShape.isRequired,
  reAuth: PropTypes.func.isRequired,
  streamOrBroadcast: PropTypes.string.isRequired,
  subjectId: PropTypes.string.isRequired,
  trackEvent: PropTypes.func.isRequired,
  broadcastHash: PropTypes.string.isRequired,
  broadcastId: PropTypes.number.isRequired,
};

export default withRouter(Clips);
